import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('tradeflow_token');
    } catch (error) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    try {
      localStorage.setItem('tradeflow_token', access_token);
    } catch (error) {
      console.warn('Unable to persist auth token:', error);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    try {
      localStorage.setItem('tradeflow_token', access_token);
    } catch (error) {
      console.warn('Unable to persist auth token:', error);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    try {
      localStorage.removeItem('tradeflow_token');
    } catch (error) {
      console.warn('Unable to clear auth token:', error);
    }
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateLanguage = async (lang) => {
    if (token) {
      await axios.put(`${API}/auth/language?language=${lang}`);
    }
  };

  const isClient = user?.role?.startsWith('client');
  const isAgent = user?.role === 'agent';
  const isPartner = user?.role?.startsWith('partner');
  const isEmployee = user?.role?.startsWith('employee');
  const isAdmin = user?.role === 'employee_admin';
  const isManager = user?.role === 'employee_manager' || isAdmin;
  const isRiskManager = user?.role === 'employee_risk' || isManager;
  const isSales = user?.role === 'employee_sales' || isManager;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateLanguage,
      isAuthenticated: !!user,
      isClient,
      isAgent,
      isPartner,
      isEmployee,
      isAdmin,
      isManager,
      isRiskManager,
      isSales
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
